import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { ToastHost } from "@/shell/Toast";
import AppShell from "@/layouts/AppShell";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import UnknownRouteRedirect from "@/components/auth/UnknownRouteRedirect";
import Home from "@/pages/Home";
import Features from "@/pages/Features";
import DemoCourse from "@/pages/DemoCourse";
import DatabaseTest from "@/pages/DatabaseTest";
import InsertItem from "@/pages/InsertItem";
import S3Upload from "@/pages/S3Upload";
import Auth from "@/pages/Auth";
import Profile from "@/pages/Profile";
import Schools from "@/pages/Schools";
import Messages from "@/pages/Messages";
import SchoolManage from "@/pages/SchoolManage";
import Courses from "@/pages/Courses";
import Teach from "@/pages/Teach";
import ClassWorkspace from "@/pages/ClassWorkspace";
import CourseBuilder from "@/pages/CourseBuilder";
import Library from "@/pages/Library";
import RoleDefaults from "@/pages/RoleDefaults";
import GradeRubrics from "@/pages/GradeRubrics";
import Events from "@/pages/Events";
import EventForm from "@/pages/EventForm";
import EventDetail from "@/pages/EventDetail";
import MyStudents from "@/pages/MyStudents";
import EmailDemo from "@/pages/EmailDemo";
import PopupDemo from "@/pages/PopupDemo";
import StudentRoot from "@/pages/student/StudentRoot";
import ParentRoot from "@/pages/parent/ParentRoot";
import "./App.css";

function LegacyClassesRedirect() {
  const { pathname, search, hash } = useLocation();
  const next = `${pathname.replace(/^\/classes(?=\/|$)/, "/teach")}${search}${hash}`;
  return <Navigate to={next} replace />;
}

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <ToastHost>
          <Router>
            <div className="App">
              <Routes>
                {/* The student app is its own shell — deliberately outside
                    AppShell, which is the staff tool. */}
                <Route
                  path="/student/*"
                  element={
                    <ProtectedRoute page="student">
                      <StudentRoot />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/parent/*"
                  element={
                    <ProtectedRoute page="parent">
                      <ParentRoot />
                    </ProtectedRoute>
                  }
                />
                {/* Public features page: its own full-width page, no sign-in, no app shell. */}
                <Route path="/features" element={<Features />} />

                <Route element={<AppShell />}>
                  <Route path="/" element={<Home />} />
                  <Route
                    path="/features/demo"
                    element={
                      <ProtectedRoute page="course-builder">
                        <DemoCourse />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/database-test" element={<DatabaseTest />} />
                  <Route path="/insert-item" element={<InsertItem />} />
                  <Route path="/s3-upload" element={<S3Upload />} />
                  <Route path="/popups" element={<PopupDemo />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/schools"
                    element={
                      <ProtectedRoute>
                        <Schools />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/parent-messages" element={<Navigate to="/messages" replace />} />
                  <Route
                    path="/messages"
                    element={
                      <ProtectedRoute page="messages">
                        <Messages />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/schools/:schoolId"
                    element={
                      <ProtectedRoute>
                        <SchoolManage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/courses/:courseId/build"
                    element={
                      <ProtectedRoute page="course-builder">
                        <CourseBuilder />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-students/*"
                    element={
                      <ProtectedRoute page="my-students">
                        <MyStudents />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/teach"
                    element={
                      <ProtectedRoute page="teach">
                        <Teach />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/teach/:courseId"
                    element={
                      <ProtectedRoute page="teach">
                        <ClassWorkspace />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/teach/:courseId/work/:submissionId"
                    element={
                      <ProtectedRoute page="teach">
                        <ClassWorkspace />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/teach/:courseId/progress/:progressId"
                    element={
                      <ProtectedRoute page="teach">
                        <ClassWorkspace />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/teach/:courseId/discussions/:discussionId"
                    element={
                      <ProtectedRoute page="teach">
                        <ClassWorkspace />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/classes/*" element={<LegacyClassesRedirect />} />
                  <Route
                    path="/courses"
                    element={
                      <ProtectedRoute>
                        <Courses />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/library"
                    element={
                      <ProtectedRoute page="library">
                        <Library />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/role-defaults"
                    element={
                      <ProtectedRoute page="role-defaults">
                        <RoleDefaults />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/events"
                    element={
                      <ProtectedRoute page="events">
                        <Events />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/events/new"
                    element={
                      <ProtectedRoute page="events">
                        <EventForm />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/events/:eventId/edit"
                    element={
                      <ProtectedRoute page="events">
                        <EventForm />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/events/:eventId"
                    element={
                      <ProtectedRoute page="events">
                        <EventDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/grade-rubrics"
                    element={
                      <ProtectedRoute page="grade-rubrics">
                        <GradeRubrics />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/email-demo"
                    element={
                      <ProtectedRoute>
                        <EmailDemo />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<UnknownRouteRedirect to="/" />} />
                </Route>
              </Routes>
            </div>
          </Router>
        </ToastHost>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
